import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environments/environment';

/**
 * Feature Usage Result interface
 */
export interface FeatureUsageResult {
    success: boolean;
    allowed: boolean;
    featureCode: string;
    subFeatureCode?: string;
    currentUsage: number;
    limit: number | null; // null means unlimited
    remaining: number | null; // null means unlimited
    message?: string;
    messageKey?: string;
    reason?: 'NO_SUBSCRIPTION' | 'SUBSCRIPTION_EXPIRED' | 'FEATURE_NOT_IN_PLAN' | 'USAGE_LIMIT_REACHED' | 'USAGE_WOULD_EXCEED';
    featureName?: { en?: string; ar?: string };
}

/**
 * Feature details with usage info
 */
export interface FeatureUsageInfo {
    featureId?: string;
    subFeatureId?: string;
    code: string;
    name?: { en: string; ar: string };
    description?: { en: string; ar: string };
    used: number;
    limit: number | null;
    remaining: number | null;
    lastUsed?: Date;
}

/**
 * User's subscription usage summary
 */
export interface SubscriptionUsageSummary {
    success: boolean;
    hasSubscription: boolean;
    subscriptionId?: string;
    planName?: { en: string; ar: string };
    startDate?: Date;
    endDate?: Date;
    features: { [code: string]: FeatureUsageInfo };
    subFeatures: { [code: string]: FeatureUsageInfo };
    aiTokensRemaining?: number;
    aiTokensTotal?: number;
    message?: string;
}

/**
 * Feature Access Service
 * 
 * This service provides methods to check feature access and track usage
 * for subscription-based features. It integrates with the backend API
 * and provides user-friendly notifications.
 * 
 * Usage Example:
 * ```typescript
 * // In your component
 * constructor(private featureAccess: FeatureAccessService) {}
 * 
 * async onButtonClick() {
 *   const canUse = await this.featureAccess.checkAndUseFeature('BOQ_AI').toPromise();
 *   if (canUse) {
 *     // Proceed with the action
 *     this.performAction();
 *   }
 *   // If not allowed, service automatically shows error notification
 * }
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class FeatureAccessService {
    private apiUrl = `${environment.apiUrl}/feature-usage`;
    
    // Cache for usage data to reduce API calls
    private usageCache$ = new BehaviorSubject<SubscriptionUsageSummary | null>(null);
    private lastCacheTime: number = 0;
    private cacheValidityMs = 60000; // Cache valid for 1 minute

    constructor(
        private http: HttpClient,
        private toastr: ToastrService,
        private translate: TranslateService
    ) {}

    /**
     * Check if user has access to a feature WITHOUT incrementing usage
     * Use this for UI display (show/hide buttons, disable elements)
     * 
     * @param featureCode The feature code to check
     * @param subFeatureCode Optional sub-feature code
     * @param showNotification Whether to show error notification if not allowed
     * @returns Observable<boolean> - true if allowed, false otherwise
     */
    canUseFeature(
        featureCode: string, 
        subFeatureCode?: string,
        showNotification: boolean = false
    ): Observable<boolean> {
        return this.http.post<FeatureUsageResult>(`${this.apiUrl}/check`, {
            featureCode,
            subFeatureCode,
            incrementUsage: false
        }).pipe(
            map(result => {
                if (!result.allowed && showNotification) {
                    this.showAccessDeniedNotification(result);
                }
                return result.allowed;
            }),
            catchError(error => {
                console.error('Feature check error:', error);
                if (showNotification) {
                    const message = error?.error?.message || this.getTranslation('feature_check_error');
                    this.toastr.error(message, this.getTranslation('access_denied'));
                }
                return of(false);
            })
        );
    }

    /**
     * Check feature access AND increment usage counter
     * Use this when the user actually performs the action
     * 
     * @param featureCode The feature code to check and use
     * @param subFeatureCode Optional sub-feature code
     * @param usageAmount Amount to increment (default: 1)
     * @param showNotification Whether to show notifications (default: true)
     * @returns Observable<boolean> - true if allowed and usage recorded, false otherwise
     */
    checkAndUseFeature(
        featureCode: string,
        subFeatureCode?: string,
        usageAmount: number = 1,
        showNotification: boolean = true
    ): Observable<boolean> {
        return this.http.post<FeatureUsageResult>(`${this.apiUrl}/check`, {
            featureCode,
            subFeatureCode,
            incrementUsage: true,
            usageAmount
        }).pipe(
            tap(result => {
                if (!result.allowed && showNotification) {
                    this.showAccessDeniedNotification(result);
                }
                // Invalidate cache since usage changed
                this.lastCacheTime = 0;
            }),
            map(result => result.allowed),
            catchError(error => {
                console.error('Feature usage error:', error);
                if (showNotification) {
                    const message = error?.error?.message || this.getTranslation('feature_check_error');
                    this.toastr.error(message, this.getTranslation('error'));
                }
                return of(false);
            })
        );
    }

    /**
     * Get detailed feature usage result (includes remaining count, etc.)
     * 
     * @param featureCode The feature code to check
     * @param subFeatureCode Optional sub-feature code
     * @param incrementUsage Whether to increment usage
     * @returns Observable<FeatureUsageResult>
     */
    getFeatureUsageDetails(
        featureCode: string,
        subFeatureCode?: string,
        incrementUsage: boolean = false
    ): Observable<FeatureUsageResult> {
        return this.http.post<FeatureUsageResult>(`${this.apiUrl}/check`, {
            featureCode,
            subFeatureCode,
            incrementUsage
        }).pipe(
            catchError(error => {
                console.error('Feature usage details error:', error);
                return of({
                    success: false,
                    allowed: false,
                    featureCode,
                    subFeatureCode,
                    currentUsage: 0,
                    limit: 0,
                    remaining: 0,
                    message: error?.error?.message || 'Error checking feature'
                } as FeatureUsageResult);
            })
        );
    }

    /**
     * Get all feature usage for current user's subscription
     * Results are cached for performance
     * 
     * @param forceRefresh Force refresh from server
     * @returns Observable<SubscriptionUsageSummary>
     */
    getMyUsage(forceRefresh: boolean = false): Observable<SubscriptionUsageSummary> {
        const now = Date.now();
        
        // Return cached data if still valid
        if (!forceRefresh && this.usageCache$.value && (now - this.lastCacheTime) < this.cacheValidityMs) {
            return of(this.usageCache$.value);
        }

        return this.http.get<SubscriptionUsageSummary>(`${this.apiUrl}/my-usage`).pipe(
            tap(result => {
                this.usageCache$.next(result);
                this.lastCacheTime = now;
            }),
            catchError(error => {
                console.error('Get usage error:', error);
                return of({
                    success: false,
                    hasSubscription: false,
                    features: {},
                    subFeatures: {},
                    message: error?.error?.message || 'Error fetching usage data'
                } as SubscriptionUsageSummary);
            })
        );
    }

    /**
     * Get remaining usage for a specific feature
     * 
     * @param featureCode Feature code
     * @param subFeatureCode Optional sub-feature code
     * @returns Observable<number | null> - null means unlimited
     */
    getRemainingUsage(featureCode: string, subFeatureCode?: string): Observable<number | null> {
        return this.getFeatureUsageDetails(featureCode, subFeatureCode, false).pipe(
            map(result => result.remaining)
        );
    }

    /**
     * Check if feature has unlimited usage
     * 
     * @param featureCode Feature code
     * @param subFeatureCode Optional sub-feature code
     * @returns Observable<boolean>
     */
    isUnlimited(featureCode: string, subFeatureCode?: string): Observable<boolean> {
        return this.getFeatureUsageDetails(featureCode, subFeatureCode, false).pipe(
            map(result => result.limit === null || result.limit === -1)
        );
    }

    /**
     * Reset feature usage (useful for testing)
     * 
     * @param featureCode Optional - reset specific feature
     * @param subFeatureCode Optional - reset specific sub-feature
     * @returns Observable<boolean>
     */
    resetUsage(featureCode?: string, subFeatureCode?: string): Observable<boolean> {
        return this.http.post<{success: boolean}>(`${this.apiUrl}/reset`, {
            featureCode,
            subFeatureCode
        }).pipe(
            tap(() => {
                // Invalidate cache
                this.lastCacheTime = 0;
            }),
            map(result => result.success),
            catchError(error => {
                console.error('Reset usage error:', error);
                return of(false);
            })
        );
    }

    /**
     * Rollback feature usage after a failed operation
     * This decrements the usage counter to restore the user's quota
     * 
     * @param featureCode The feature code to rollback
     * @param subFeatureCode Optional sub-feature code
     * @param usageAmount Amount to rollback (default: 1)
     * @returns Observable<boolean>
     */
    rollbackFeatureUsage(
        featureCode: string,
        subFeatureCode?: string,
        usageAmount: number = 1
    ): Observable<boolean> {
        return this.http.post<{success: boolean; message?: string}>(`${this.apiUrl}/rollback`, {
            featureCode,
            subFeatureCode,
            usageAmount
        }).pipe(
            tap(() => {
                // Invalidate cache since usage changed
                this.lastCacheTime = 0;
            }),
            map(result => result.success),
            catchError(error => {
                console.error('Rollback usage error:', error);
                return of(false);
            })
        );
    }

    /**
     * Clear the usage cache
     */
    clearCache(): void {
        this.usageCache$.next(null);
        this.lastCacheTime = 0;
    }

    /**
     * Subscribe to usage cache updates
     */
    get usageCache(): Observable<SubscriptionUsageSummary | null> {
        return this.usageCache$.asObservable();
    }

    /**
     * Show appropriate notification for access denied
     * Priority: NO_SUBSCRIPTION > SUBSCRIPTION_EXPIRED > FEATURE_NOT_IN_PLAN > USAGE_LIMIT_REACHED
     */
    private showAccessDeniedNotification(result: FeatureUsageResult): void {
        const isArabic = this.translate.currentLang === 'ar';
        
        let title: string;
        let message: string;
        let notificationType: 'error' | 'warning' = 'warning';

        // Get feature name in current language
        const featureName = result.featureName 
            ? (isArabic ? result.featureName.ar : result.featureName.en) || result.featureCode
            : result.featureCode;

        // Handle based on reason (priority order already set by backend)
        switch (result.reason) {
            case 'NO_SUBSCRIPTION':
                title = isArabic ? 'لا يوجد اشتراك' : 'No Active Subscription';
                message = isArabic 
                    ? 'لا يوجد اشتراك نشط. يرجى الاشتراك في خطة للمتابعة.'
                    : 'You do not have an active subscription. Please subscribe to a plan to continue.';
                notificationType = 'error';
                break;
                
            case 'SUBSCRIPTION_EXPIRED':
                title = isArabic ? 'انتهى الاشتراك' : 'Subscription Expired';
                message = isArabic 
                    ? 'انتهت صلاحية اشتراكك. يرجى تجديد خطتك للمتابعة.'
                    : 'Your subscription has expired. Please renew your plan to continue.';
                notificationType = 'error';
                break;
                
            case 'FEATURE_NOT_IN_PLAN':
                title = isArabic ? 'الميزة غير متوفرة' : 'Feature Not Available';
                message = isArabic 
                    ? `خطتك الحالية لا تتضمن ميزة "${featureName}". يرجى ترقية خطتك.`
                    : `Your current plan does not include the "${featureName}" feature. Please upgrade your plan.`;
                break;
                
            case 'USAGE_LIMIT_REACHED':
                title = isArabic ? 'تم الوصول للحد الأقصى' : 'Usage Limit Reached';
                message = isArabic 
                    ? `لقد وصلت إلى الحد الأقصى لاستخدام "${featureName}" (${result.limit}). يرجى ترقية خطتك.`
                    : `You have reached the usage limit for "${featureName}" (${result.limit}). Please upgrade your plan.`;
                break;
                
            case 'USAGE_WOULD_EXCEED':
                title = isArabic ? 'تجاوز الحد المسموح' : 'Would Exceed Limit';
                message = isArabic 
                    ? `هذا الإجراء سيتجاوز الحد المسموح لـ "${featureName}". المتبقي: ${result.remaining}`
                    : `This action would exceed the limit for "${featureName}". Remaining: ${result.remaining}`;
                break;
                
            default:
                // Fallback for legacy messages or unknown reasons
                title = isArabic ? 'الوصول مرفوض' : 'Access Denied';
                message = result.message || (isArabic 
                    ? 'لا يمكنك استخدام هذه الميزة. يرجى ترقية خطتك.'
                    : 'You cannot use this feature. Please upgrade your plan.');
        }

        // Show notification based on type
        if (notificationType === 'error') {
            this.toastr.error(message, title, {
                timeOut: 6000,
                closeButton: true,
                progressBar: true
            });
        } else {
            this.toastr.warning(message, title, {
                timeOut: 5000,
                closeButton: true,
                progressBar: true
            });
        }
    }

    /**
     * Get translation or fallback
     */
    private getTranslation(key: string): string {
        const translation = this.translate.instant(key);
        return translation !== key ? translation : this.getDefaultTranslation(key);
    }

    /**
     * Default translations
     */
    private getDefaultTranslation(key: string): string {
        const defaults: { [key: string]: string } = {
            'feature_check_error': 'Error checking feature access',
            'access_denied': 'Access Denied',
            'error': 'Error',
            'usage_limit_reached': 'Usage Limit Reached',
            'please_upgrade': 'Please upgrade your plan'
        };
        return defaults[key] || key;
    }
}
